import { gql } from "@apollo/client";
import { PRODUCT_ID_FIELDS } from "../fragments/product";

const FIND_PLAID_LINKED_ASSETS = gql`
  query FindPlaidLinkedAssets($accountLogin: String, $institution: String, $plaidAccessToken: ID) {
    findPlaidLinkedAssets(
      accountLogin: $accountLogin
      institution: $institution
      plaidAccessToken: $plaidAccessToken
    ) {
      _id
      accountNumber
      plaidAccountId
      plaidAccessToken
      totalHoldings {
        accountCashHoldings
        accountStockHoldings
      }
      meta {
        isDeleted
        plaidUpdateSuccessful
        notes
      }
    }
  }
`;

const FIND_ALL_CRYPTO_ASSETS = gql`
  query FindAllCryptoAssets {
    findAllCryptoAssets {
      assets {
        ... on CryptoAsset {
          _id
          assetName
          assetType
          accountLogin
          meta {
            notes
          }
          xpub
          walletProvider
          totalHoldings {
            accountCryptoHoldings
            cryptoCoins {
              ticker
              quantity
            }
          }
          product {
            ... on DafProduct {
              _id
              name
              users {
                _id
                associationType
                user {
                  ... on DonorUser {
                    _id
                    firstName
                    lastName
                  }
                }
              }
            }
            ... on ClatProduct {
              _id
              name
              users {
                _id
                associationType
                user {
                  ... on DonorUser {
                    _id
                    firstName
                    lastName
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const FIND_ALL_LENDING_ASSETS = gql`
  query LendingAsset {
    findAllLendingAssets {
      assets {
        ... on LendingAsset {
          _id
          assetName
          assetType
          acquisitionMethod
          loanAmount
          meta {
            notes
          }
          totalHoldings {
            interestReceived
            outstandingBalance
            principalReceived
            totalReceived
          }
          product {
            ... on DafProduct {
              _id
              name
              users {
                _id
                associationType
                user {
                  ... on DonorUser {
                    _id
                    firstName
                    lastName
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const FIND_ALL_EQUITY_ASSETS = gql`
  query FindAllEquityAssets {
    findAllEquityAssets {
      assets {
        ... on EquityAsset {
          _id
          assetName
          assetType
          meta {
            notes
          }
          product {
            ... on DafProduct {
              _id
              name
              users {
                _id
                associationType
                user {
                  ... on DonorUser {
                    _id
                    firstName
                    lastName
                  }
                }
              }
            }
          }
          acquisitionMethod
          appraisalDate
          appraisalReceived
          assetStartDate
          assetPendingTransactionTotal
          committedCapital
          createdAt
          form8283Signed
          lastUpdated {
            date
          }
          saleDocsReceived
          totalHoldings {
            capitalCalledToDate
            date
            marketValue
            manualCalculation
          }
          updatedAt
        }
      }
    }
  }
`;

const FIND_ALL_PENDING_EQUITY_DONATIONS = gql`
  query FindAllPendingEquityDonations {
    findAllPendingEquityDonations {
      asset {
        _id
        assetName
        status
        totalHoldings {
          date
          marketValue
        }
        meta {
          reminders {
            reminderType
            reminderRecipient
            reminderDate
          }
        }
        product {
          ... on DafProduct {
            _id
            name
            users {
              _id
              associationType
              user {
                ... on DonorUser {
                  _id
                  firstName
                  lastName
                  email
                  advisor {
                    _id
                    firstName
                    lastName
                    email
                  }
                }
              }
            }
          }
        }
        documents {
          _id
          createdAt
          fileCategory
        }
      }
      transactions {
        ... on EquityContributionTransaction {
          _id
          donationDate
          transactionAmount
          transactionStatus
          transactionType
          documents {
            _id
            createdAt
            fileCategory
          }
          meta {
            reminders {
              reminderType
              reminderRecipient
              reminderDate
            }
          }
        }
        ... on EquitySaleTransaction {
          _id
          transactionAmount
          transactionStatus
          transactionType
          documents {
            _id
            createdAt
            fileCategory
          }
        }
        ... on EquitySaleProceedsTransaction {
          _id
          transactionAmount
          transactionStatus
          transactionType
          documents {
            _id
            createdAt
            fileCategory
          }
        }
      }
      count {
        appraisal
        equityContribution
        equitySale
        equitySaleProceeds
        form8282
        form8283
        saleDocument
      }
    }
  }
`;

const FIND_ACCOUNT_NUMBERS_BY_PRODUCT_ID = gql`
  query FindAccountNumbersByProductId($productId: ID!) {
    findAccountNumbersByProductId(productId: $productId) {
      _id
      accountNumber
    }
  }
`;

const FIND_CRYPTO_TICKERS_BY_PRODUCT_ID = gql`
  query FindCryptoTickersByProductId($productId: ID!) {
    findCryptoTickersByProductId(productId: $productId) {
      ticker
      quantity
    }
  }
`;

const FIND_ASSETS_BY_PRODUCT_ID = gql`
  query FindAssetsByProductId($productId: ID!) {
    findAssetsByProductId(productId: $productId) {
      ... on AccountAsset {
        _id
        assetName
        assetType
        institution
        accountLogin
        accountNumber
        lastUpdated {
          date
          userId
        }
        updatedAt
        totalHoldings {
          accountCashHoldings
          accountStockHoldings
          accountCryptoHoldings
          date
          lastRelevantHoldingsUpdateDate
        }
        meta {
          notes
        }
      }
      ... on CryptoAsset {
        _id
        assetName
        assetType
        xpub
        walletProvider
        accountLogin
        countedInBucket
        countedInReason
        lastUpdated {
          date
          userId
        }
        updatedAt
        totalHoldings {
          accountCryptoHoldings
          cryptoCoins {
            ticker
            quantity
          }
        }
        meta {
          notes
        }
      }
      ... on EquityAsset {
        _id
        assetName
        assetType
        committedCapital
        appraisalDate
        form8283Signed
        saleDocsReceived
        acquisitionMethod
        sourceCollection
        countedInBucket
        countedInReason
        source {
          ... on OrganizationEntity {
            _id
          }
          ... on EquitySourceAsset {
            _id
          }
        }
        totalHoldings {
          date
          marketValue
          manualCalculation
          capitalCalledToDate
        }
        meta {
          notes
        }
      }
      ... on FundAsset {
        _id
        assetName
        assetType
        countedInBucket
        countedInReason
        lastUpdated {
          date
          userId
        }
        updatedAt
        meta {
          notes
        }
      }
      ... on LendingAsset {
        _id
        assetName
        assetType
        acquisitionMethod
        loanAmount
        countedInBucket
        countedInReason
        sourceCollection
        source {
          ... on OrganizationEntity {
            _id
          }
          ... on EquitySourceAsset {
            _id
          }
        }
        entity {
          _id
        }
        totalHoldings {
          date
          principalReceived
          interestReceived
          totalReceived
          outstandingBalance
        }
        updatedAt
        meta {
          notes
        }
      }
      ... on OtherAsset {
        _id
        assetName
        assetType
        countedInBucket
        countedInReason
        lastUpdated {
          date
          userId
        }
        updatedAt
        meta {
          notes
        }
      }
      ... on RecoverableGrantAsset {
        _id
        assetName
        assetType
        lastUpdated {
          date
          userId
        }
        updatedAt
        meta {
          notes
        }
      }
      ... on InternalAccountAsset {
        _id
        assetName
        assetType
        lastUpdated {
          date
          userId
        }
        updatedAt
        meta {
          notes
        }
      }
    }
  }
`;

const FIND_ASSETS_WITH_DOCUMENTS_BY_PRODUCT_ID = gql`
  query FindAssetsWithDocumentsByProductId($productId: ID!, $asExternalUser: Boolean, $includeDeleted: Boolean) {
    findAssetsWithDocumentsByProductId(productId: $productId, asExternalUser: $asExternalUser, includeDeleted: $includeDeleted) {
      ... on EquityAsset {
        _id
        assetName
        meta { isDeleted }
        documents {
          _id
          createdAt
          executionDate
          fileCategory
          status
          signedBy
          fileName
          fileType
          gridFsFileId
          internalOnly
        }
      }
      ... on AccountAsset {
        _id
        assetName
        meta { isDeleted }
        documents {
          _id
          createdAt
          executionDate
          fileCategory
          status
          signedBy
          fileName
          fileType
          gridFsFileId
          internalOnly
        }
      }
      ... on CryptoAsset {
        _id
        assetName
        meta { isDeleted }
        documents {
          _id
          createdAt
          executionDate
          fileCategory
          status
          signedBy
          fileName
          fileType
          gridFsFileId
          internalOnly
        }
      }
      ... on LendingAsset {
        _id
        assetName
        meta { isDeleted }
        documents {
          _id
          createdAt
          executionDate
          fileCategory
          status
          signedBy
          fileName
          fileType
          gridFsFileId
          internalOnly
        }
      }
    }
  }
`;

const FIND_ASSETS_BY_TYPE = gql`
  query FindAssetsByType($assetType: AssetType!) {
    findAssetsByType(assetType: $assetType) {
      ... on EquitySourceAsset {
        _id
        assetName
        sourceAssetType
        friendlyId
      }
    }
  }
`;

const FIND_ASSETS_BY_SOURCE_ID = gql`
  query FindAssetsBySourceId($sourceId: ID!) {
    findAssetsBySourceId(sourceId: $sourceId) {
      ... on EquityAsset {
        _id
        assetName
        assetType
        friendlyId
        owner {
          _id
        }
        denormalized {
          donorName
          productName
        }
        ${PRODUCT_ID_FIELDS}
      }
      ... on LendingAsset {
        _id
        assetName
        assetType
        friendlyId
        owner {
          _id
        }
        denormalized {
          donorName
          productName
        }
        ${PRODUCT_ID_FIELDS}
      }
    }
  }
`;

const RUN_DUNHAM_HOLDINGS = gql`
  query RunDunhamHoldingsParser($documentId: ID, $custodianLoginId: ID) {
    runDunhamHoldingsParser(documentId: $documentId, custodianLoginId: $custodianLoginId) {
      ... on AccountAsset {
        _id
        accountLogin
        accountNumber
        assetName
        institution
        totalHoldings {
          accountCashHoldings
          accountStockHoldings
        }
      }
    }
  }
`;

const RUN_MORGAN_STANLEY_PARSER = gql`
  query RunMorganStanleyHoldingsParser($documentId: ID, $custodianLoginId: ID) {
    runMorganStanleyHoldingsParser(documentId: $documentId, custodianLoginId: $custodianLoginId) {
      ... on AccountAsset {
        _id
        accountLogin
        accountNumber
        assetName
        institution
        totalHoldings {
          accountCashHoldings
          accountStockHoldings
        }
      }
    }
  }
`;

const RUN_PERSHING_PARSER = gql`
  query RunPershingHoldingsParser($documentId: ID, $custodianLoginId: ID) {
    runPershingHoldingsParser(documentId: $documentId, custodianLoginId: $custodianLoginId) {
      ... on AccountAsset {
        _id
        accountLogin
        accountNumber
        assetName
        institution
        totalHoldings {
          accountCashHoldings
          accountStockHoldings
        }
      }
    }
  }
`;

const RUN_RAYMOND_JAMES_PARSER = gql`
  query RunRaymondJamesHoldingsParser($documentId: ID, $custodianLoginId: ID) {
    runRaymondJamesHoldingsParser(documentId: $documentId, custodianLoginId: $custodianLoginId) {
      ... on AccountAsset {
        _id
        accountLogin
        accountNumber
        assetName
        institution
        totalHoldings {
          accountCashHoldings
          accountStockHoldings
        }
      }
    }
  }
`;

const RUN_CHARLES_SCHWAB_PARSER = gql`
  query RunCharlesSchwabHoldingsParser($accountLogin: String, $documentId: ID, $custodianLoginId: ID) {
    runCharlesSchwabHoldingsParser(
      accountLogin: $accountLogin
      documentId: $documentId
      custodianLoginId: $custodianLoginId
    ) {
      ... on AccountAsset {
        _id
        accountLogin
        accountNumber
        assetName
        institution
        totalHoldings {
          accountCashHoldings
          accountStockHoldings
        }
      }
    }
  }
`;

const RUN_CHASE_HOLDINGS_PARSER = gql`
  query RunChaseHoldingsParser($documentId: ID, $custodianLoginId: ID) {
    runChaseHoldingsParser(documentId: $documentId, custodianLoginId: $custodianLoginId) {
      ... on AccountAsset {
        _id
        accountLogin
        accountNumber
        assetName
        institution
        totalHoldings {
          accountCashHoldings
          accountStockHoldings
        }
      }
    }
  }
`;

const RUN_TRADE_PMR_PARSER = gql`
  query RunTradePMRHoldingsParser($documentId: ID, $custodianLoginId: ID) {
    runTradePMRHoldingsParser(documentId: $documentId, custodianLoginId: $custodianLoginId) {
      ... on AccountAsset {
        _id
        accountLogin
        accountNumber
        assetName
        institution
        totalHoldings {
          accountCashHoldings
          accountStockHoldings
        }
      }
    }
  }
`;

const GET_CSV_HOLDINGS_STATUS = gql`
  query GetCsvHoldingsStatus($institution: String) {
    getCsvHoldingsStatus(institution: $institution) {
      totalCsvAssets
      totalOutOfDateAssets
      status
      oldestUpdate
      assets {
        _id
        accountNumber
        updateSuccessful
        totalHoldings {
          date
          accountCashHoldings
          accountStockHoldings
          accountTotalHoldings
        }
      }
    }
  }
`;

const RUN_HOLDINGS = gql`
  query Query {
    runHoldings
  }
`;

const RUN_FIDELITY_HOLDINGS = gql`
  query RunFidelityHoldingsParser($documentId: ID, $custodianLoginId: ID) {
    runFidelityHoldingsParser(documentId: $documentId, custodianLoginId: $custodianLoginId) {
      ... on AccountAsset {
        _id
      }
    }
  }
`;

const RUN_GET_CUSTODIAN_CSV_PUPPETEER = gql`
  query RunGetCustodianHoldingsCsvPuppeteerHeadless($institutionName: String!, $username: String!) {
    runGetCustodianHoldingsCsvPuppeteerHeadless(
      institutionName: $institutionName
      username: $username
    ) {
      ok
      message
      code
    }
  }
`;

const RUN_CRYPTO_HOLDINGS_UPDATE_EST_VALUE = gql`
  query RunCryptoCoinsEstValue {
    runCryptoCoinsEstValue
  }
`;

const FIND_OPPORTUNITY_ASSETS = gql`
  query FindOpportunityAssets($status: String) {
    findOpportunityAssets(status: $status) {
      ... on OpportunityAsset {
        _id
        status
        feeAmount
        probability
        source
        opportunityType
        closeDate
        meta {
          notes
        }
        assignee {
          _id
          firstName
          lastName
          email
        }
        advisor {
          _id
          firstName
          lastName
          email
          type
        }
        owner {
          _id
          firstName
          lastName
          email
          type
        }
        denormalized {
          donorName
          productName
        }
        anticipatedAum
        assetName
        assetType
        createdAt
        friendlyId
        updatedAt
      }
    }
  }
`;

const FIND_ASSETS_SERVER_SIDE = gql`
  query FindAssetsServerSide($productId: ID, $input: ServerQueryInput, $includeDeleted: Boolean) {
    findAssetsServerSide(productId: $productId, input: $input, includeDeleted: $includeDeleted) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      assets {
        ... on AccountAsset {
          _id
          friendlyId
          assetName
          assetType
          institution
          accountLogin
          accountNumber
          mainFundingAccount
          owner {
            _id
          }
          denormalized {
            donorName
            productName
          }
          ${PRODUCT_ID_FIELDS}
          totalHoldings {
            accountCashHoldings
            accountStockHoldings
            lastRelevantHoldingsUpdateDate
          }
          meta {
            isDeleted
            notes
          }
          labels {
            _id
            name
            color
          }
        }
        ... on CryptoAsset {
          _id
          friendlyId
          assetName
          assetType
          walletProvider
          accountLogin
          xpub
          countedInBucket
          countedInReason
          owner {
            _id
          }
          denormalized {
            donorName
            productName
          }
          ${PRODUCT_ID_FIELDS}
          totalHoldings {
            accountCryptoHoldings
            cryptoCoins {
              ticker
              quantity
            }
            lastRelevantHoldingsUpdateDate
          }
          meta {
            isDeleted
            notes
          }
          labels {
            _id
            name
            color
          }
        }
        ... on EquityAsset {
          _id
          friendlyId
          assetName
          assetType
          acquisitionMethod
          committedCapital
          saleDocsReceived
          form8283Signed
          appraisalDate
          countedInBucket
          countedInReason
          owner {
            _id
          }
          denormalized {
            donorName
            productName
          }
          ${PRODUCT_ID_FIELDS}
          totalHoldings {
            marketValue
            capitalCalledToDate
            manualCalculation
            lastRelevantHoldingsUpdateDate
          }
          meta {
            isDeleted
            notes
          }
          labels {
            _id
            name
            color
          }
        }
        ... on LendingAsset {
          _id
          friendlyId
          assetName
          assetType
          acquisitionMethod
          loanAmount
          countedInBucket
          countedInReason
          owner {
            _id
          }
          denormalized {
            donorName
            productName
          }
          ${PRODUCT_ID_FIELDS}
          totalHoldings {
            outstandingBalance
            principalReceived
            interestReceived
            totalReceived
            lastRelevantHoldingsUpdateDate
          }
          meta {
            isDeleted
            notes
          }
          labels {
            _id
            name
            color
          }
        }
      }
    }
  }
`;

const GET_ASSET_DEPENDENCY_COUNTS = gql`
  query GetAssetDependencyCounts($assetId: ID!) {
    getAssetDependencyCounts(assetId: $assetId) {
      transactions
      documents
      feeTransactions
    }
  }
`;

const SEARCH_ASSETS_FOR_LINKING = gql`
  query SearchAssetsForLinking($ownerId: ID, $productId: ID, $opportunitiesOnly: Boolean, $searchTerm: String, $limit: Int) {
    searchAssetsForLinking(ownerId: $ownerId, productId: $productId, opportunitiesOnly: $opportunitiesOnly, searchTerm: $searchTerm, limit: $limit) {
      ... on EquityAsset {
        _id
        assetName
        assetType
        friendlyId
        owner { _id firstName lastName }
        product {
          ... on DafProduct { _id name }
          ... on ClatProduct { _id name }
          ... on BrandingProduct { _id name }
          ... on CommunityFundProduct { _id name }
        }
      }
      ... on LendingAsset {
        _id
        assetName
        assetType
        friendlyId
        owner { _id firstName lastName }
        product {
          ... on DafProduct { _id name }
          ... on ClatProduct { _id name }
          ... on BrandingProduct { _id name }
          ... on CommunityFundProduct { _id name }
        }
      }
      ... on AccountAsset {
        _id
        assetName
        assetType
        friendlyId
        owner { _id firstName lastName }
        product {
          ... on DafProduct { _id name }
          ... on ClatProduct { _id name }
          ... on BrandingProduct { _id name }
          ... on CommunityFundProduct { _id name }
        }
      }
      ... on CryptoAsset {
        _id
        assetName
        assetType
        friendlyId
        owner { _id firstName lastName }
        product {
          ... on DafProduct { _id name }
          ... on ClatProduct { _id name }
          ... on BrandingProduct { _id name }
          ... on CommunityFundProduct { _id name }
        }
      }
      ... on OpportunityAsset {
        _id
        assetName
        assetType
        friendlyId
        status
        owner { _id firstName lastName }
        product {
          ... on DafProduct { _id name }
          ... on ClatProduct { _id name }
          ... on BrandingProduct { _id name }
          ... on CommunityFundProduct { _id name }
        }
      }
    }
  }
`;

export {
  FIND_PLAID_LINKED_ASSETS,
  FIND_ALL_LENDING_ASSETS,
  FIND_ALL_CRYPTO_ASSETS,
  FIND_CRYPTO_TICKERS_BY_PRODUCT_ID,
  FIND_ALL_EQUITY_ASSETS,
  FIND_ALL_PENDING_EQUITY_DONATIONS,
  FIND_ACCOUNT_NUMBERS_BY_PRODUCT_ID,
  FIND_ASSETS_BY_PRODUCT_ID,
  FIND_ASSETS_WITH_DOCUMENTS_BY_PRODUCT_ID,
  RUN_DUNHAM_HOLDINGS,
  RUN_MORGAN_STANLEY_PARSER,
  RUN_PERSHING_PARSER,
  RUN_RAYMOND_JAMES_PARSER,
  RUN_CHARLES_SCHWAB_PARSER,
  RUN_CHASE_HOLDINGS_PARSER,
  RUN_TRADE_PMR_PARSER,
  FIND_ASSETS_BY_TYPE,
  FIND_ASSETS_BY_SOURCE_ID,
  GET_CSV_HOLDINGS_STATUS,
  RUN_HOLDINGS,
  RUN_FIDELITY_HOLDINGS,
  RUN_GET_CUSTODIAN_CSV_PUPPETEER,
  RUN_CRYPTO_HOLDINGS_UPDATE_EST_VALUE,
  FIND_OPPORTUNITY_ASSETS,
  FIND_ASSETS_SERVER_SIDE,
  GET_ASSET_DEPENDENCY_COUNTS,
  SEARCH_ASSETS_FOR_LINKING,
};
